import {
  DownloadOutlined,
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
} from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import { Button, Flex, Image, Modal, Spin, Typography } from "antd";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
  getFileTypeIcon,
  isBrowserRenderableImageContentType,
  isPdfContentType,
} from "../utils/getFileTypeIcon";
import styles from "./FilePreviewModal.module.css";

export type PreviewableFile = {
  id?: string;
  content_type?: string | null;
  name?: string | null;
};

export type FilePreviewState = ReturnType<typeof useFilePreview>;

/**
 * Previews one file at a time out of `files`, the list the preview was opened from,
 * so the modal can step to the previous/next file of that same list.
 */
export function useFilePreview(files: readonly (PreviewableFile | null | undefined)[]) {
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);

  const navigableFiles = useMemo(
    () => files.filter((file): file is PreviewableFile => Boolean(file?.id)),
    [files]
  );

  const currentIndex = previewFileId
    ? navigableFiles.findIndex((file) => file.id === previewFileId)
    : -1;
  const previewFile = currentIndex >= 0 ? navigableFiles[currentIndex] : null;

  const openPreview = useCallback(
    (file: PreviewableFile) => setPreviewFileId(file.id ?? null),
    []
  );
  const closePreview = useCallback(() => setPreviewFileId(null), []);

  const showFileAt = useCallback(
    (index: number) => {
      const file = navigableFiles[index];
      if (file?.id) setPreviewFileId(file.id);
    },
    [navigableFiles]
  );
  const showPrevious = useCallback(
    () => showFileAt(currentIndex - 1),
    [showFileAt, currentIndex]
  );
  const showNext = useCallback(
    () => showFileAt(currentIndex + 1),
    [showFileAt, currentIndex]
  );

  return {
    previewFile,
    currentIndex,
    totalFiles: navigableFiles.length,
    openPreview,
    closePreview,
    showPrevious,
    showNext,
  };
}

function PdfPreviewFrame({
  url,
  name,
  fallback,
}: {
  url: string;
  name: string;
  fallback: ReactNode;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | undefined;
    setBlobUrl(null);
    setHasFailed(false);

    fetch(url, { signal: controller.signal })
      .then((response) => {
        const contentType = response.headers.get("content-type") ?? "";
        if (!response.ok || !contentType.includes("application/pdf")) {
          throw new Error(`Failed to load PDF: ${response.status} ${contentType}`);
        }
        return response.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch((error: Error) => {
        if (error.name !== "AbortError") {
          setHasFailed(true);
        }
      });

    return () => {
      controller.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [url]);

  if (hasFailed) {
    return <>{fallback}</>;
  }

  if (!blobUrl) {
    return <Spin indicator={<LoadingOutlined spin />} size="large" />;
  }

  return <iframe src={blobUrl} title={name} className={styles.pdfFrame} />;
}

function isTypingInFormField(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
  );
}

type FilePreviewModalProps = {
  preview: FilePreviewState;
  getDownloadUrl: (file: PreviewableFile) => string;
  intl: IntlShape;
};

export function FilePreviewModal({
  preview,
  getDownloadUrl,
  intl,
}: FilePreviewModalProps) {
  const {
    previewFile: file,
    currentIndex,
    totalFiles,
    closePreview,
    showPrevious,
    showNext,
  } = preview;
  const [isImageZoomOpen, setIsImageZoomOpen] = useState(false);

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < totalFiles - 1;
  const showNavigation = totalFiles > 1;

  useEffect(() => {
    if (!file || isImageZoomOpen) return;

    const navigateWithArrowKeys = (event: KeyboardEvent) => {
      if (isTypingInFormField(event.target)) return;
      if (event.key === "ArrowLeft" && hasPrevious) {
        event.preventDefault();
        showPrevious();
      } else if (event.key === "ArrowRight" && hasNext) {
        event.preventDefault();
        showNext();
      }
    };

    window.addEventListener("keydown", navigateWithArrowKeys);
    return () => window.removeEventListener("keydown", navigateWithArrowKeys);
  }, [file, isImageZoomOpen, hasPrevious, hasNext, showPrevious, showNext]);

  if (!file) return null;

  const isImage = isBrowserRenderableImageContentType(file.content_type);
  const isPdf = isPdfContentType(file.content_type);
  const fileUrl = file.id ? getDownloadUrl(file) : undefined;

  const fallbackContent = (
    <Flex vertical align="center" gap="middle">
      <span className={styles.fallbackIcon}>
        {getFileTypeIcon({ contentType: file.content_type, name: file.name })}
      </span>
      <Typography.Text type="secondary">
        {intl.formatMessage({ id: "ticket_file_preview_unavailable" })}
      </Typography.Text>
    </Flex>
  );

  const renderPreviewContent = () => {
    if (isImage && fileUrl) {
      return (
        <Image
          src={fileUrl}
          alt={file.name ?? undefined}
          classNames={{ image: styles.previewImage }}
          preview={{ onOpenChange: setIsImageZoomOpen }}
        />
      );
    }
    if (isPdf && fileUrl) {
      return (
        <PdfPreviewFrame
          url={fileUrl}
          name={file.name ?? "preview"}
          fallback={fallbackContent}
        />
      );
    }
    return fallbackContent;
  };

  return (
    <Modal
      open
      onCancel={() => {
        setIsImageZoomOpen(false);
        closePreview();
      }}
      title={
        <Flex align="baseline" gap="small">
          <Typography.Text ellipsis={{ tooltip: file.name }} className={styles.fileName}>
            {file.name}
          </Typography.Text>
          {showNavigation && (
            <Typography.Text type="secondary">
              {intl.formatMessage(
                { id: "file_preview_position" },
                { current: currentIndex + 1, total: totalFiles }
              )}
            </Typography.Text>
          )}
        </Flex>
      }
      centered
      width="min(90vw, 1000px)"
      footer={[
        <Typography.Link
          key="download"
          href={getDownloadUrl(file)}
          download={file.name ?? true}
        >
          <Button type="primary" icon={<DownloadOutlined />}>
            {intl.formatMessage({ id: "ticket_file_download" })}
          </Button>
        </Typography.Link>,
      ]}
    >
      <Flex align="center" gap="small" className={styles.previewBody}>
        {showNavigation && (
          <Button
            shape="circle"
            icon={<LeftOutlined />}
            disabled={!hasPrevious}
            onClick={showPrevious}
            aria-label={intl.formatMessage({ id: "file_preview_previous" })}
            className={styles.navigationButton}
          />
        )}
        <Flex
          key={file.id}
          justify="center"
          align="center"
          className={styles.previewContent}
        >
          {renderPreviewContent()}
        </Flex>
        {showNavigation && (
          <Button
            shape="circle"
            icon={<RightOutlined />}
            disabled={!hasNext}
            onClick={showNext}
            aria-label={intl.formatMessage({ id: "file_preview_next" })}
            className={styles.navigationButton}
          />
        )}
      </Flex>
    </Modal>
  );
}
